import React, { useState } from "react";

import { Checkbox, styled, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { FileViewer } from "components/popups/Viewer/FileViewer";
import { DealBase, IFile } from "store/types";

import {
  BORDER_DIVIDER,
  FilesTableRow,
  getPreviewType,
  StyledTableCellBase,
  StyledTableCellCheckbox
} from "./FilesTableRow";
import { FileValues } from "./FilesUpload";

const StyledTableContainer = styled(TableContainer)({
  borderRadius: "8px",
  border: BORDER_DIVIDER,
  overflow: "hidden"
});

const StyledHeaderRow = styled(TableRow)({
  backgroundColor: "#f6f8fc"
});

const StyledTableCellHeader = styled(StyledTableCellBase)({
  fontSize: 14,
  fontWeight: 500,
  color: "#071f35",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
});

const StyledTableCellHeaderFirst = styled(StyledTableCellHeader)({
  pl: 2,
  pr: 1.25
});

export interface Props {
  filesValues: FileValues[];
  setFilesValues: React.Dispatch<React.SetStateAction<FileValues[]>>;
  updateDocumentType: (index: number, value: string | null) => void;
  updateEffectiveDate?: (index: number, value: string | null) => void;
  dealInvestors: { id: number; name: string }[];
  dealInvestorsLoading: boolean;
  selected: Set<number>;
  setSelected: React.Dispatch<React.SetStateAction<Set<number>>>;
  isCloudMirror?: boolean;
  dealInvestorsMap?: Record<number, { id: number; name: string }[]>;
  onDealChange?: (index: number, deals: DealBase[]) => void;
  lockedDealId?: number | null;
  isClientWorkspace?: boolean;
}

export const FilesTable: React.FC<Props> = ({
  filesValues,
  setFilesValues,
  updateDocumentType,
  updateEffectiveDate,
  selected,
  setSelected,
  dealInvestors,
  dealInvestorsLoading,
  isCloudMirror,
  dealInvestorsMap,
  onDealChange,
  lockedDealId
}) => {
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelected(event.target.checked ? new Set(filesValues.map((_, i) => i)) : new Set());
  };

  const handleSelectOne = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = new Set(selected);
    if (event.target.checked) next.add(index);
    else next.delete(index);
    setSelected(next);
  };

  // Preview the selected (not yet uploaded) file in-app via FileViewer using an object URL.
  const [previewFile, setPreviewFile] = useState<Partial<IFile> | null>(null);

  const handlePreview = (file: File) => {
    const type = getPreviewType(file);
    if (!type) return;
    setPreviewFile({ name: file.name, type, file_url: URL.createObjectURL(file) });
  };

  const handleClosePreview = () => {
    setPreviewFile((prev) => {
      if (prev?.file_url) URL.revokeObjectURL(prev.file_url);
      return null;
    });
  };

  const handleRemove = (index: number) => {
    setFilesValues((prev) => prev.filter((_, i) => i !== index));
    // Remove the row from the selection and shift indices after it to stay in sync.
    setSelected((prev) => {
      const next = new Set<number>();
      prev.forEach((i) => {
        if (i === index) return;
        next.add(i > index ? i - 1 : i);
      });
      return next;
    });
  };

  // if (!files.length) return null;

  const colWidths = isCloudMirror
    ? { name: "22%", deal: "20%", classification: "15%", investor: "20%", date: "13%" }
    : { name: "27%", deal: undefined, classification: "20%", investor: "30%", date: "13%" };

  return (
    <StyledTableContainer sx={{ maxHeight: 250, overflow: "auto" }}>
      <Table stickyHeader size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
        <TableHead>
          <StyledHeaderRow>
            <StyledTableCellCheckbox padding="checkbox" $header>
              <Checkbox
                indeterminate={selected.size > 0 && selected.size < filesValues.length}
                checked={selected.size === filesValues.length && filesValues.length > 0}
                onChange={handleSelectAll}
                sx={{ p: 0.5 }}
              />
            </StyledTableCellCheckbox>
            <StyledTableCellHeaderFirst sx={{ width: colWidths.name }}>File Name</StyledTableCellHeaderFirst>
            {isCloudMirror && <StyledTableCellHeader sx={{ width: colWidths.deal }}>Deal</StyledTableCellHeader>}
            <StyledTableCellHeader sx={{ width: colWidths.classification }}>Classification</StyledTableCellHeader>
            <StyledTableCellHeader sx={{ width: colWidths.investor }}>Investor</StyledTableCellHeader>
            <StyledTableCellHeader sx={{ width: colWidths.date }}>Effective Date</StyledTableCellHeader>
            <StyledTableCellHeader align="center" sx={{ width: 20, px: 0.5 }} />
          </StyledHeaderRow>
        </TableHead>
        <TableBody>
          {filesValues.map((fileValue, index) => (
            <FilesTableRow
              key={`${fileValue.file.name}-${index}`}
              fileValue={fileValue}
              index={index}
              isSelected={selected.has(index)}
              isCloudMirror={isCloudMirror}
              lockedDealId={lockedDealId}
              disableRemove={filesValues.length === 1}
              setFilesValues={setFilesValues}
              updateDocumentType={updateDocumentType}
              updateEffectiveDate={updateEffectiveDate}
              onDealChange={onDealChange}
              dealInvestors={dealInvestors}
              dealInvestorsLoading={dealInvestorsLoading}
              dealInvestorsMap={dealInvestorsMap}
              onSelect={handleSelectOne(index)}
              onPreview={handlePreview}
              onRemove={handleRemove}
            />
          ))}
        </TableBody>
      </Table>
      <FileViewer
        file={previewFile ?? undefined}
        isOpen={Boolean(previewFile)}
        onClose={handleClosePreview}
        hideActions
      />
    </StyledTableContainer>
  );
};
